import { z } from 'zod';
import {
  ehrPatientSummarySchema,
  ehrDemographicsSchema,
  ehrContactSchema,
  ehrInsuranceSchema,
  ehrCareTeamSchema,
  ehrMedicationSchema,
} from './patient';
import { ehrProviderAvailabilitySchema, ehrTimeSlotSchema } from './provider';
import {
  ehrPatientAppointmentsSchema,
  createAppointmentRequestSchema,
  createAppointmentResponseSchema,
  cancelAppointmentRequestSchema,
  rescheduleAppointmentRequestSchema,
} from './appointment';

/**
 * Standard response wrapper with fetchedAt timestamp.
 */
export const ehrDataResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    fetchedAt: z.string(), // ISO 8601 datetime
  });

/**
 * Patient lookup request.
 * At least one identifier must be provided.
 */
export const patientLookupRequestSchema = z.object({
  phone: z.string().optional(),
  mrn: z.string().optional(),
  dateOfBirth: z.string().optional(), // ISO 8601 date (YYYY-MM-DD)
  lastName: z.string().optional(),
  firstName: z.string().optional(),
});

export type PatientLookupRequest = z.infer<typeof patientLookupRequestSchema>;

/**
 * Patient lookup response.
 */
export const patientLookupResponseSchema = z.object({
  patient: ehrPatientSummarySchema.nullable(),
});

export type PatientLookupResponse = z.infer<typeof patientLookupResponseSchema>;

/**
 * Get patient demographics response.
 */
export const getPatientDemographicsResponseSchema = ehrDataResponseSchema(ehrDemographicsSchema);
export type GetPatientDemographicsResponse = z.infer<typeof getPatientDemographicsResponseSchema>;

/**
 * Get patient contact response.
 */
export const getPatientContactResponseSchema = ehrDataResponseSchema(ehrContactSchema);
export type GetPatientContactResponse = z.infer<typeof getPatientContactResponseSchema>;

/**
 * Get patient insurance response.
 */
export const getPatientInsuranceResponseSchema = ehrDataResponseSchema(ehrInsuranceSchema);
export type GetPatientInsuranceResponse = z.infer<typeof getPatientInsuranceResponseSchema>;

/**
 * Get patient care team response.
 */
export const getPatientCareTeamResponseSchema = ehrDataResponseSchema(ehrCareTeamSchema);
export type GetPatientCareTeamResponse = z.infer<typeof getPatientCareTeamResponseSchema>;

/**
 * Get patient medications response.
 */
export const getPatientMedicationsResponseSchema = ehrDataResponseSchema(
  z.array(ehrMedicationSchema)
);
export type GetPatientMedicationsResponse = z.infer<typeof getPatientMedicationsResponseSchema>;

/**
 * Get patient appointments response.
 */
export const getPatientAppointmentsResponseSchema = ehrPatientAppointmentsSchema;
export type GetPatientAppointmentsResponse = z.infer<typeof getPatientAppointmentsResponseSchema>;

/**
 * Provider availability request query params.
 */
export const providerAvailabilityQuerySchema = z.object({
  locationId: z.string(),
  startDate: z.string(), // ISO 8601 date (YYYY-MM-DD)
  endDate: z.string(), // ISO 8601 date (YYYY-MM-DD)
  appointmentTypeCode: z.string().optional(),
});

export type ProviderAvailabilityQuery = z.infer<typeof providerAvailabilityQuerySchema>;

/**
 * Provider availability response.
 */
export const getProviderAvailabilityResponseSchema = ehrProviderAvailabilitySchema;
export type GetProviderAvailabilityResponse = z.infer<typeof getProviderAvailabilityResponseSchema>;

/**
 * Create appointment request.
 */
export const createAppointmentApiRequestSchema = createAppointmentRequestSchema;
export type CreateAppointmentApiRequest = z.infer<typeof createAppointmentApiRequestSchema>;

/**
 * Create appointment response.
 */
export const createAppointmentApiResponseSchema = createAppointmentResponseSchema;
export type CreateAppointmentApiResponse = z.infer<typeof createAppointmentApiResponseSchema>;

/**
 * Cancel appointment request.
 */
export const cancelAppointmentApiRequestSchema = cancelAppointmentRequestSchema;
export type CancelAppointmentApiRequest = z.infer<typeof cancelAppointmentApiRequestSchema>;

/**
 * Reschedule appointment request.
 */
export const rescheduleAppointmentApiRequestSchema = rescheduleAppointmentRequestSchema;
export type RescheduleAppointmentApiRequest = z.infer<typeof rescheduleAppointmentApiRequestSchema>;

/**
 * Health check response.
 */
export const ehrHealthCheckResponseSchema = z.object({
  status: z.enum(['ok', 'degraded', 'error']),
  ehrConnection: z.enum(['connected', 'disconnected', 'error']),
  message: z.string().optional(),
  timestamp: z.string(), // ISO 8601 datetime
});

export type EhrHealthCheckResponse = z.infer<typeof ehrHealthCheckResponseSchema>;

/**
 * EHR error response.
 */
export const ehrErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});

export type EhrErrorResponse = z.infer<typeof ehrErrorResponseSchema>;

/**
 * Unified EHR Adapter API Contract.
 *
 * All EHR adapters must implement these endpoints:
 *
 * POST   /patients/lookup                    - Lookup patient by identifiers
 * GET    /patients/:id/demographics          - Get patient demographics
 * GET    /patients/:id/contact               - Get patient contact info
 * GET    /patients/:id/insurance             - Get patient insurance
 * GET    /patients/:id/care-team             - Get patient care team
 * GET    /patients/:id/medications           - Get patient medications
 * GET    /patients/:id/appointments          - Get patient appointments
 * GET    /providers/:id/availability         - Get provider availability
 * POST   /appointments                       - Book appointment
 * DELETE /appointments/:id                   - Cancel appointment
 * PUT    /appointments/:id/reschedule        - Reschedule appointment
 * GET    /health                             - Health check
 */
export interface EhrAdapterContract {
  // Patient lookup
  lookupPatient(request: PatientLookupRequest): Promise<PatientLookupResponse>;

  // Patient data (by ehrPatientId)
  getPatientDemographics(ehrPatientId: string): Promise<GetPatientDemographicsResponse>;
  getPatientContact(ehrPatientId: string): Promise<GetPatientContactResponse>;
  getPatientInsurance(ehrPatientId: string): Promise<GetPatientInsuranceResponse>;
  getPatientCareTeam(ehrPatientId: string): Promise<GetPatientCareTeamResponse>;
  getPatientMedications(ehrPatientId: string): Promise<GetPatientMedicationsResponse>;
  getPatientAppointments(ehrPatientId: string): Promise<GetPatientAppointmentsResponse>;

  // Provider availability (by ehrProviderId)
  getProviderAvailability(
    ehrProviderId: string,
    query: ProviderAvailabilityQuery
  ): Promise<GetProviderAvailabilityResponse>;

  // Appointment management
  createAppointment(request: CreateAppointmentApiRequest): Promise<CreateAppointmentApiResponse>;
  cancelAppointment(ehrAppointmentId: string, reason?: string): Promise<void>;
  rescheduleAppointment(
    ehrAppointmentId: string,
    request: RescheduleAppointmentApiRequest
  ): Promise<CreateAppointmentApiResponse>;

  // Health check
  healthCheck(): Promise<EhrHealthCheckResponse>;
}
